import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Link, Tooltip, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ReactComponent as FilterIcon } from "../../../src/assets/Customer/FilterIcon.svg";
import { ReactComponent as FilterIconHover } from "../../../src/assets/Customer/FilterIconHover.svg";
import { ReactComponent as CreateIcon } from "../../../src/assets/USA_Images/CreateIcon.svg";
import { REACT_ADD_EDIT_DAY_LIGHT_TIME } from "../../actions/EndPoints";
import DataTableLatest from "../../components/DataTable/DataTableLatest";
import DeleteDialog from "../../components/DialogBoxComponent/DeleteDialog";
import HeaderToolbar from "../../components/HeaderToolbar";
import Toast from "../../components/ToastContainer/CustomToast";
import EditIcon from "../../Pages/Person/EditIcon";
import {
  deleteDayLightTime,
  getDayLightTime,
} from "../../slices/DayLightTimeSlice";
import { ThemeContext } from "../../theme/ThemeContext";
import DayLightFilter from "./DayLightFilter";

function DayLightTime() {
  const { t } = useTranslation();
  const [clear, setClear] = useState(false);
  const { darkMode } = useContext(ThemeContext);
  const [isHovered, setIsHovered] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(1);
  const [pageSize, setPageSize] = useState(0);
  const dispatch = useDispatch();
  const [openFilter, setOpenFilter] = useState(false);
  const history = useHistory();

  const handleEditVisit = (rowData) => {
    history.push({
      pathname: REACT_ADD_EDIT_DAY_LIGHT_TIME,
      state: {
        data: rowData,
      },
    });
  };

  const handleDelete = (row) => {
    dispatch(deleteDayLightTime(row.id))
      .unwrap()
      .then((res) => {
        if (res.code === "MSGS4085") {
          Toast(res.message, "success");
          loadData(paging, filter);
          return;
        } else {
          Toast(res.message, "error");
          return;
        }
      });
  };

  const [paging, setPaging] = useState({
    page: 1,
    rowsPerPage: 50,
  });

  const handleChange = (newPage, size) => {
    setPage(newPage);
    setRowsPerPage(size);
    setPaging({
      page: newPage,
      rowsPerPage: size,
    });
  };

  const [filter, setFilter] = useState({
    name: "",
    timeZone: "",
  });

  const defaultFilter = {
    name: "",
    timeZone: "",
  };

  useEffect(() => {
    loadData(paging, filter);
  }, [filter, paging]);

  const loadData = (paging, filter) => {
    let payload = {
      name: filter.name,
      timeZone: filter.timeZone,
      pageNumber: paging.page,
      pageSize: paging.rowsPerPage,
    };
    dispatch(getDayLightTime(payload))
      .unwrap()
      .then((res) => {
        if (res.code === "MSGS4000") {
          setRows(res.data.data);
          setTotalRecords(res.data.totalCount);
          setPage(res.data.curPage);
          setPageSize(res.data.pageSize);
          setTotalPages(res.data.totalPages);
        }
      });
  };

  const convertCode = (code) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const weekOrdinals = ["First", "Second", "Third", "Fourth", "Fifth"];
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    let monthNumber = parseInt(code.substring(0, 2), 10); // 11 -> November
    let weekOrder = parseInt(code.substring(2, 4), 10); // 01 -> First
    let dayOfWeek = parseInt(code.substring(4, 6), 10); // 00 -> Sunday
    let time = code.substring(6, 8); // 02 -> Time (unclear)

    // Adjust month number for zero-indexed array
    let monthName = monthNames[monthNumber - 1];
    let weekOrdinal = weekOrdinals[weekOrder - 1];
    let dayName = dayNames[dayOfWeek];

    return `${monthName} ${weekOrdinal} ${dayName} ${time}Hour`;
  };

  const columns = React.useMemo(() => [
    {
      field: "name",
      headerName: t("accessLevelName"),
      flex: 1,
      width: 150,
      valueGetter: (params) => params.row.name,
    },
    {
      field: "startTime",
      headerName: t("textFieldInviteScheduledDate"),
      flex: 1,
      width: 200,
      renderCell: (params) => {
        const startime = convertCode(params.row.startTime);
        return (
          <Tooltip
            title={startime}
            placement="top"
            disableInteractive
          >
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {startime}
            </div>
          </Tooltip>
        );
      },
      valueGetter: (params) => params.row.startTime,
    },
    {
      field: "endTime",
      headerName: t("textFieldInviteScheduledEndDate"),
      flex: 1,
      width: 300,
      renderCell: (params) => {
        const endTime = convertCode(params.row.endTime);
        return (
          <Tooltip
            title={endTime}
            placement="top"
            disableInteractive
          >
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {endTime}
            </div>
          </Tooltip>
        );
      },
      valueGetter: (params) => params.row.endTime,
    },

    {
      field: "timeZone",
      headerName: t("ADD010"),
      flex: 1,
      width: 100,
      renderCell: (params) => <div>{"UTC" + params.row.timeZone}</div>,
      valueGetter: (params) => params.row.timeZone,
    },
    {
      field: "Actions",
      headerName: t("ACTION"),
      type: "actions",
      flex: 1,
      width: 250,
      headerAlign: "center",
      getActions: (params) => {
        let companyId = params.row.companyId;
        if (companyId !== null && companyId !== undefined) {
          return [
            <Box onClick={() => handleEditVisit(params.row)} key="edit">
              <EditIcon />
            </Box>,
            <Box key="delete">
              <DeleteDialog
                handleDelete={() => handleDelete(params.row)}
                text={"Are you sure you want to delete the "}
                name={"selected record"}
              />
            </Box>,
          ];
        }
        return [];
      },
    },
  ], []);

  const handleFilter = (payload) => {
    setClear(true);
    setFilter({
      name: payload.name,
      timeZone: payload.timeZone,
    });
    setOpenFilter(!openFilter);
  };

  const resetFilter = () => {
    setClear(false);
    setFilter(defaultFilter);
  };
  const handleCreate = () => {
    history.push({
      pathname: REACT_ADD_EDIT_DAY_LIGHT_TIME,
      state: {
        data: {},
      },
    });
  };

  return (
    <>
      <HeaderToolbar title={t("DAY_LIGHT_TIME")} />
      <Box
        display={"flex"}
        width={"100%"}
        flexDirection={"row-reverse"}
        alignItems={"center"}
        gap="3px"
      >
        <Box item>
          {clear && (
            <Link
              href="#"
              underline="hover"
              className="line"
              style={{
                opacity: "1",
                color: "#E3393C",
                fontSize: "14px",
                underline: "hover",
                display: "inline",
              }}
              onClick={resetFilter}
            >
              {t("CLEARFILTER")}
            </Link>
          )}
        </Box>
        <Tooltip title={t("COMMON009")}>
          <Box item className={"addIcon"} onClick={handleCreate}>
            <CreateIcon />
          </Box>
        </Tooltip>

        <Tooltip title={t("COMMON002")}>
          <Box item
            className="refreshIcon"
          >
            <RefreshIcon onClick={() => loadData(paging, filter)} />
          </Box>
        </Tooltip>

        <Box item>
          <Tooltip
            title={
              <Typography fontSize={12} fontWeight={500}>
                {t("COMMON006")}
              </Typography>
            }
          >
            <span
              className={darkMode ? "filterdark" : "filter"}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => {
                setOpenFilter(!openFilter);
              }}
            >
              {isHovered ? (
                <FilterIconHover
                  style={{
                    cursor: "pointer",
                    color: darkMode ? "#fff" : "#474a4e",
                  }}
                ></FilterIconHover>
              ) : (
                <FilterIcon
                  style={{
                    cursor: "pointer",
                    color: darkMode ? "#fff" : "#474a4e",
                  }}
                ></FilterIcon>
              )}
            </span>
          </Tooltip>
        </Box>
      </Box>

      <div
        style={{
          paddingTop: "10px",
        }}
      >
        <DataTableLatest
          columns={columns}
          rows={rows}
          page={page}
          pageCount={totalPages}
          count={totalRecords}
          pageSize={rowsPerPage}
          handleChange={(newPage, size) => handleChange(newPage, size)}
        />
      </div>

      {openFilter && (
        <DayLightFilter
          open={openFilter}
          handleClose={() => {
            setOpenFilter(!openFilter);
          }}
          handleFilter={handleFilter}
        />
      )}
    </>
  );
}

export default DayLightTime;
